import { RootPage } from '../page';
import $api from '../../api';
import { TourModel } from '../../models/Gen/BaseModels';
import * as siteConfig from '../../site-config/config';
import languages from '../../utils/languages';

import CardEditor from './components/cardEditor';
import EditorIframePositionBar from '../../components/editor/editorIframePositionBar';
import EditorSettingsComponent from '../../components/editor/editorSettings';
import AnimationDescriptor from '../../components/descriptors/animationDescriptor';
import BackdropDescriptor from '../../components/descriptors/backdropDescriptor';
import RulesDescriptor from '../../components/descriptors/rulesDescriptor';
import AdvanceActionDescriptor from '../../components/descriptors/advanceActionDescriptor';
import PositionValue from '../../components/descriptors/positionValue';

import EditorSettings from '../../models/EditorSettings';

import Switcher from '../../components/switcher';
import createUUID from '../../utils/uuid';
import templates from '../../common/card-editor/templates';
import { isFlagOn } from '../../utils/flags';

class TourEditorView extends RootPage {
    constructor() {
        super();

        Switcher();

        this.isLoaded = ko.observable(false);

        this.isDirty = ko.observable();

        this.tour = ko.observable();
        this.selectedCard = ko.observable(null);

        const saveButtonState = ko.pureComputed(() => {
            return 'btn btn-success ' + (this.isDirty() ? ' btn-dot' : '');
        });

        const getProperSaveButtons = () => {
            if (!window.auth.isImpVo) {
                if (isFlagOn('editor_new_save')) {
                    return {
                        css: saveButtonState, onclick: () => { this.save() }, text: 'Save', subActions: [
                            { text: 'Save & Publish changes', onclick: this.saveAndPublish.bind(this), icon: 'fas fa-paper-plane' },
                            { text: 'Save & Close editor', onclick: this.saveAndClose.bind(this), icon: 'fas fa-sign-out-alt' },
                        ]
                    }
                } else {
                    return { css: 'btn btn-success', onclick: this.saveAndClose.bind(this), text: 'Save & Close' }
                }
            } else {
                return null
            }
        }

        window.topToolbarButtons = [
            getProperSaveButtons(),
            { css: 'btn fas fa-cog text-white mr-2', onclick: this.toggleSettings.bind(this), title: 'Settings' },
            { css: 'btn fas fa-question text-white mr-0', onclick: this.showHelp.bind(this, 'editor_tours'), title: 'Help' },
            { css: 'btn fas fa-eye text-white ' + (isFlagOn('editor_new_save') ? 'mr-5' : 'mr-4'), onclick: this.preview.bind(this), title: 'Preview from start' },
            { css: 'text-white', html: '<i class="fas fa-eye"></i>&nbsp;<i class="fas fa-level-down-alt"></i>', onclick: this.previewFromSelected.bind(this), visible: ko.computed(() => !!this.selectedCard()), title: 'Preview from this card' }
        ];

        CardEditor(this);

        EditorIframePositionBar(this);
        EditorSettingsComponent(this);

        AnimationDescriptor(this);
        BackdropDescriptor(this);
        RulesDescriptor(this);
        AdvanceActionDescriptor(this);
        PositionValue(this);

        window.addEventListener('message', (msg) => {
            if (msg.data.action === 'elementSelected') {
                console.log('MS', msg.data);
                document.dispatchEvent(new CustomEvent('cardElementQueried', { detail: { cardId: msg.data.cardId, query: msg.data.query, url: msg.data.url, iframe: msg.data.iframe } }));
                this.autosave();
            } else if (msg.data.action === 'saveCardContent') {
                document.dispatchEvent(new CustomEvent('cardContentSet', { detail: { cardId: msg.data.cardId, content: msg.data.content } }));
                this.autosave();
            } else if (msg.data.action === 'envInfoResponse') {
                document.dispatchEvent(new CustomEvent('envInfoResponse', { detail: { cardId: msg.data.cardId, url: msg.data.url, urlPath: msg.data.urlPath } }));
            } else if (msg.data.action === 'closeCard') {
                document.dispatchEvent(new CustomEvent('deselectCard', { detail: { cardId: msg.data.cardId } }));
            } else if (msg.data.action === 'relocateCard') {
                document.dispatchEvent(new CustomEvent('relocateCard', { detail: { cardId: msg.data.cardId } }));
            } else if (msg.data.action === 'add_card_template') {
                this.addCard({ type: msg.data.type, templateType: msg.data.templateType });
            } else if (msg.data.action === 'resume-card-editing') {
                document.dispatchEvent(new CustomEvent('cardResumeEditing', { detail: { cardId: msg.data.cardId } }))
            } else if (msg.data.action === 'elementSelectorChanged') {
                document.dispatchEvent(new CustomEvent('cardSelectorChanged', { detail: { cardId: msg.data.cardId, query: msg.data.query } }));
                this.autosave();
            }
        });

        window.onerror = (e) => {
            this.logSaveThanSorry();
        }

        this.cardTypes = [
            { type: 'element', name: 'Element highlight' },
            { type: 'dialog', name: 'Dialog' }
        ];
        this.cardTypeSelectorShown = ko.observable(false);

        this.settingsShown = ko.observable(false);
        this.editorSettings = new EditorSettings();

        this.supportedLanguages = window.auth.project.SupportedLanguages.map(lng => {
            return languages.filter(i => i.alpha2 == lng)[0];
        }); //languages.filter(i => window.auth.project.SupportedLanguages.indexOf(i.alpha2) !== -1);
        this.selectedLanguage = ko.observable(window.auth.project.SupportedLanguages[0]);
        this.selectedLanguage.subscribe(lng => {
            window.parent.postMessage({ action: 'changeLanguage', language: this.selectedLanguage() }, '*');
        });

        this.hasPreviousCard = ko.computed(() => {
            if (!this.selectedCard()) return false;

            const cards = ko.unwrap(this.getDescriptor().cards);

            return cards.findIndex(i => ko.unwrap(i.id) == this.selectedCard().card.id()) > 0;
        });

        this.hasNextCard = ko.computed(() => {
            if (!this.selectedCard()) return false;

            const cards = ko.unwrap(this.getDescriptor().cards);

            return cards.findIndex(i => ko.unwrap(i.id) == this.selectedCard().card.id()) < cards.length - 1;
        });

        this.isInDefaultLanguage = ko.pureComputed(() => {
            return this.selectedLanguage() == this.supportedLanguages[0].alpha2;
        });
    }

    onInit() {
        siteConfig.CardDescriptor.prototype._ModelExtender = function (model, p, pp, mappingOptions) {
            mappingOptions.cardPosition = {
                create: function (options) {
                    return new siteConfig.PositionValue(options.data);
                }
            };
            mappingOptions.backdrop = {
                create: function (options) {
                    return new siteConfig.BackdropDescriptor(options.data);
                }
            };
            mappingOptions.advanceAction = {
                create: function (options) {
                    return new siteConfig.AdvanceActionDescriptor(options.data);
                }
            };
        }

        TourModel.prototype._ModelExtender = function (model, p, pp, mappingOptions) {
            mappingOptions.descriptorObject = {
                create: function (options) {
                    return new siteConfig.TourDescriptor(options.data);
                }
            };
        };

        var tourAutosave = sessionStorage.getItem('tourAutosave');

        if (tourAutosave) {
            this.tour(new TourModel(JSON.parse(tourAutosave)));
        } else {
            this.tour(new TourModel(this.getServerSideData('tour')));
        }

        this.isLoaded(true);

        this.showWelcomeModalIfNeeded();
    }

    showWelcomeModalIfNeeded() {
        try {
            if (window.auth && window.auth.user && window.auth.user.SignUpAt >= '2022-01-19') {
                const welcomeShown = localStorage.getItem('pf_editor_welcome_dialog_shown');

                if (!welcomeShown) {
                    $api.users.getSettings(
                        { key: 'tour_welcome_shown' },
                        {},
                        (val) => {
                            if (!val || Object.keys(val).length === 0) {
                                $api.users.setSettings(
                                    {},
                                    { key: 'tour_welcome_shown', value: '1' },
                                    () => {
                                    }
                                );

                                this.showHelp('tour-welcome');
                            }

                            localStorage.setItem('pf_editor_welcome_dialog_shown', '1');
                        }
                    );
                }
            }
        } catch (e) {
            console.log('Welcome dialog err', e);
        }
    }

    autosave() {
        const objToSave = JSON.stringify(this.tour().toJS(), (k, v) => (k === '__ko_mapping__' || k === '_ModelExtender' || k === 'toJS') ? undefined : v);

        try {
            const oldSavedObj = sessionStorage.getItem('tourAutosave');
            requestAnimationFrame(() => {
                if (!!oldSavedObj && oldSavedObj != objToSave) {
                    document.dispatchEvent(new Event('pfruits_editor_dirty'));
                    this.isDirty(true);
                }
            });

            sessionStorage.setItem('tourAutosave', objToSave);
            window.parent.postMessage({ action: 'pf-set-editor-dirty' }, '*');
        } catch (e) {
            this.logSaveThanSorry();
            alert('We have encountered an error while autosaving. Save your work by clicking on the Save and exit and open the editor again.');
        }
    }

    deselectCard() {
        this.autosave();

        document.dispatchEvent(new CustomEvent('closeCard', { detail: { cardId: ko.toJS(this.selectedCard().card.id) } }));
        document.dispatchEvent(new CustomEvent('deselectCard', { detail: { cardId: ko.toJS(this.selectedCard().card.id) } }));
    }

    goToPrevCard() {
        /** @type {Array} */
        const cards = ko.unwrap(this.getDescriptor().cards);
        const currentIndex = cards.findIndex(i => ko.unwrap(i.id) == this.selectedCard().card.id())

        this.deselectCard();

        let newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
        const newCard = cards[newIndex];

        if (newCard) {
            document.dispatchEvent(new CustomEvent('selectCard', { detail: { cardId: ko.toJS(newCard.id) } }));
        }
    }

    goToNextCard() {
        /** @type {Array} */
        const cards = ko.unwrap(this.getDescriptor().cards);
        const currentIndex = cards.findIndex(i => ko.unwrap(i.id) == this.selectedCard().card.id())

        this.deselectCard();

        let newIndex = currentIndex < cards.length ? currentIndex + 1 : currentIndex;
        const newCard = cards[newIndex];

        if (newCard) {
            document.dispatchEvent(new CustomEvent('selectCard', { detail: { cardId: ko.toJS(newCard.id) } }));
        }
    }

    showHelp(id) {
        window.parent.postMessage({
            action: 'pf_show_help',
            id
        }, '*');
    }

    getDescriptor() {
        return this.tour().descriptorObject();
    }

    showCardTypeSelector() {
        this.cardTypeSelectorShown(true);
    }

    addStepFromTemplate() {
        window.parent.postMessage({ action: 'pf_show_template_selector', type: 'tours' }, '*');
    }

    addCard(type) {
        var descriptor = this.getDescriptor();

        if (descriptor.cards() === null) {
            descriptor.cards([]);
        }

        let nextBtnTexts = null;
        let skipBtnTexts = null;
        let backBtnTexts = null;

        try {
            const lastCard = descriptor.cards()[descriptor.cards().length - 1];

            nextBtnTexts = ko.unwrap((ko.unwrap(ko.unwrap(lastCard.content).footer).nextButton).text)
            skipBtnTexts = ko.unwrap((ko.unwrap(ko.unwrap(lastCard.content).footer).skipButton).text)
            backBtnTexts = ko.unwrap((ko.unwrap(ko.unwrap(lastCard.content).footer).backButton).text)
        } catch (e) {
            ///console.error('(try-catch) Cannot read texts from the previous card.');
        }

        var newStepName = 'Step #' + (descriptor.cards().length + 1);

        var newCard = new siteConfig.CardDescriptor();

        newCard.name(newStepName);

        var backdrop = new siteConfig.BackdropDescriptor();
        //backdrop.visible(true);
        //backdrop.backgroundColor('#00000033');
        if (type.type == 'element') {
            backdrop.type('boxed_bg');
        } else {
            backdrop.type('blocking');
        }

        newCard.backdrop(backdrop);

        /** HEADER & FOOTER CONTENT START */
        var titleContent = new siteConfig.TourCardTitleDescriptor();
        titleContent.visible(false);
        titleContent.text(generateTextForSupportedLanguages(newStepName));

        var footerContent = new siteConfig.TourCardFooterDescriptor();
        footerContent.visible(true);

        var nextButton = new siteConfig.ButtonInputDescriptor();
        nextButton.text(generateTextForSupportedLanguages('Next', nextBtnTexts));
        nextButton.visible(true);
        var backButton = new siteConfig.ButtonInputDescriptor();
        backButton.text(generateTextForSupportedLanguages('Back', backBtnTexts));
        backButton.visible(false);
        var skipButton = new siteConfig.ButtonInputDescriptor();
        skipButton.text(generateTextForSupportedLanguages('Skip', skipBtnTexts));
        skipButton.visible(true);

        footerContent.nextButton(nextButton);
        footerContent.backButton(backButton);
        footerContent.skipButton(skipButton);

        var content = new siteConfig.CardContentDescriptor();
        content.title(titleContent);
        content.footer(footerContent);

        /** CONTENT START */
        if (!type.templateType) {
            if (type.type === 'element') {
                content.body(generateDefaultRowForSupportedLanguages('Describe the element here.'));
            } else {
                content.body(generateDefaultRowForSupportedLanguages('Type your content here.'));
            }

            newCard.content(content);
        } else {
            const template = templates[type.templateType];

            content.body(template.body);

            newCard.content(content);
        }
        /** CONTENT END */

        newCard.url(new siteConfig.StringValue());

        newCard.displayType(type.type);

        const cardPosition = new siteConfig.PositionValue();

        if (type.type == 'dialog') {
            cardPosition.width('440px');
            cardPosition.minHeight('350px');
        } else {
            cardPosition.width('340px');
        }

        newCard.cardPosition(cardPosition);

        descriptor.cards.push(newCard);

        this.cardTypeSelectorShown(false);

        this.autosave();

        function generateTextForSupportedLanguages(text, override) {
            let lngObj = {};
            for (const lng of window.auth.project.SupportedLanguages) {
                const overridenText = override ? ko.unwrap(override[lng]) : text;
                lngObj[lng] = overridenText;
            }
            return lngObj;
        }

        function generateDefaultRowForSupportedLanguages(text) {
            let lngObj = {};
            for (const lng of window.auth.project.SupportedLanguages) {
                lngObj[lng] = [{
                    row: 0,
                    text: {
                        [lng]: text
                    }
                }];
            }
            return lngObj;
        }
    }

    setSelectedCard(card) {
        this.selectedCard(card);
    }

    removeCard(card) {
        var index = this.getDescriptor().cards.indexOf(card);
        if (index !== -1) {
            this.getDescriptor().cards.splice(index, 1);
        }
        this.autosave();
    }

    duplicateCard(card) {
        const newCard = new siteConfig.CardDescriptor(ko.mapping.toJS(card));
        newCard.id(createUUID());
        this.getDescriptor().cards.push(newCard);

        this.autosave();
    }

    preview() {
        window.parent.postMessage({ action: 'preview-tour', descriptor: JSON.stringify(ko.toJS(this.getDescriptor())) }, '*');
    }

    previewFromSelected() {
        const cardId = ko.unwrap(this.selectedCard().card.id);
        window.parent.postMessage({ action: 'preview-tour', descriptor: JSON.stringify(ko.toJS(this.getDescriptor())), firstCard: cardId }, '*');
    }

    toggleSettings() {
        if (this.settingsShown()) {
            this.editorSettings.saveToLocalStorage();
        }
        this.settingsShown(!this.settingsShown());
    }

    hideSettings() {
        this.settingsShown(false);
    }

    hasDynamicUrl(card) {
        try {
            const url = ko.unwrap(card.url().parametrized);

            return url.indexOf('[dynamic]') !== -1 || url.indexOf('[d.') !== -1;
        } catch (e) { }
    }

    hasNextStepDifferentUrl(index) {
        const cards = ko.unwrap(this.tour().descriptorObject().cards);

        if (!cards || cards.length === index + 1) return false;

        if (this.tour().descriptorObject()) {

            const prevCard = cards[index + 1];

            if (!prevCard) return false;

            return ko.unwrap(prevCard.url().parametrized) !== ko.unwrap(cards[index].url().parametrized);
        } else {
            return false;
        }
    }

    saveAndPublish() {
        this.save(() => {
            $api.tours.publish(
                { projectCode: auth.project.UniqueCode, id: this.tour().id() },
                {},
                resp => {

                }
            );
        });
    }

    saveAndClose() {
        this.save(() => {
            window.parent.close();
        });
    }

    save(saved, error) {
        $api.tours.save(
            { projectCode: auth.project.UniqueCode, id: this.tour().id() },
            { tour: ko.mapping.toJS(this.tour()) },
            (resp) => {
                this.isDirty(false);

                if (saved) saved();
            },
            (err) => {
                if (error) error(err);
                this.logSaveThanSorry();
            },
            (err) => {
                if (error) error(err);
                this.logSaveThanSorry();
            }
        );
    }

    logSaveThanSorry() {
        const objToSave = JSON.stringify(this.tour().toJS(), (k, v) => (k === '__ko_mapping__' || k === '_ModelExtender' || k === 'toJS') ? undefined : v);

        let tId = null;
        try {
            tId = this.tour().id();
        } catch (e) {

        }

        $api.projects.autosaveThing(
            { projectCode: auth.project.UniqueCode },
            { ref: window.location.href, tourId: tId, content: objToSave },
            () => { }
        );
    }

    formatCardType(cardType) {
        var ct = this.cardTypes.filter(i => i.type === cardType);

        if (ct.length > 0) {
            return ct[0].name;
        }
        else {
            this.cardTypes[0].name;
        }
    }

    copyContentFromBaseLanguage() {
        if (confirm('Do you really want to copy all content from the default language to the selected language? The content will be overwritten.')) {
            window.parent.postMessage({ action: 'copy_content', defaultLanguage: this.supportedLanguages[0].alpha2, targetLanguage: this.selectedLanguage() }, '*');
        }
    }
}

export default TourEditorView;